import React from 'react';
import SimStyleSpectrum from '@/features/services/media/components/SimStyleSpectrum';
import MasterFrequencyResponse from '@/features/services/media/components/MasterFrequencyResponse';
import OutcomeTrackLineAudioPlayer, { type OutcomeTrackLineAudioPlayerProps } from './OutcomeTrackLineAudioPlayer';
import { useAudioElementAnalyserTap } from './useAudioElementAnalyserTap';

export type OutcomeTrackVisualizerMode = 'spectrum' | 'frequency-response' | 'none';

export interface OutcomeTrackVisualizerConfig {
  mode?: OutcomeTrackVisualizerMode;
  title?: string;
  height?: number;
  spectrumColor?: string;
  spectrumMaxFreqHz?: number;
}

export interface OutcomeTrackLineAudioPlayerWithVisualizerProps extends OutcomeTrackLineAudioPlayerProps {
  visualizer?: OutcomeTrackVisualizerConfig;
  visualization?: OutcomeTrackVisualizerMode;
  visualizationTitle?: string;
  visualizationHeight?: number;
  spectrumColor?: string;
  spectrumMaxFreqHz?: number;
}

export default function OutcomeTrackLineAudioPlayerWithVisualizer({
  visualizer,
  visualization = 'spectrum',
  visualizationTitle,
  visualizationHeight = 160,
  spectrumColor = '#9fffd8',
  spectrumMaxFreqHz = 20_000,
  onAudioElement,
  ...playerProps
}: OutcomeTrackLineAudioPlayerWithVisualizerProps) {
  const { playerAnalyser, playerStatus, handleAudioElement, resumeContext } = useAudioElementAnalyserTap(onAudioElement);

  const resolvedMode = visualizer?.mode ?? visualization;
  const resolvedTitle = visualizer?.title ?? visualizationTitle;
  const resolvedHeight = visualizer?.height ?? visualizationHeight;
  const resolvedSpectrumColor = visualizer?.spectrumColor ?? spectrumColor;
  const resolvedSpectrumMaxFreqHz = visualizer?.spectrumMaxFreqHz ?? spectrumMaxFreqHz;

  const title = resolvedTitle ?? (
    resolvedMode === 'frequency-response'
      ? 'Master Frequency Response'
      : 'Spectrum Analyzer'
  );

  const helperText =
    playerStatus === 'playing'
      ? (resolvedMode === 'frequency-response' ? 'live response curve' : 'live frequency response')
      : playerAnalyser
        ? (resolvedMode === 'frequency-response' ? 'press play to drive the response analyser' : 'press play to drive the analyser')
        : 'connecting analyser…';

  return (
    <div className="space-y-3">
      <OutcomeTrackLineAudioPlayer
        {...playerProps}
        onAudioElement={handleAudioElement}
        onResumeContext={resumeContext}
      />

      {resolvedMode !== 'none' && (
        <div className="space-y-1">
          <div className="px-0.5 text-[11px] text-neutral-400 uppercase tracking-[0.18em]">
            {title}
          </div>
          <div
            className={
              `rounded-md border border-neutral-800 overflow-hidden w-full ${
                resolvedMode === 'frequency-response' ? 'bg-transparent' : 'bg-black/60'
              }`
            }
            style={{ height: resolvedHeight }}
          >
            {resolvedMode === 'frequency-response' ? (
              <MasterFrequencyResponse
                analyser={playerAnalyser}
                status={playerStatus}
                width={600}
                height={resolvedHeight}
                background="transparent"
              />
            ) : (
              <SimStyleSpectrum
                analyser={playerAnalyser}
                status={playerStatus}
                color={resolvedSpectrumColor}
                maxFreqHz={resolvedSpectrumMaxFreqHz}
              />
            )}
          </div>
          <div className="px-0.5 text-[10px] text-neutral-600 font-mono">
            {helperText}
          </div>
        </div>
      )}
    </div>
  );
}